import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedFooterModule } from '@markant-platform/shared/footer';
import { MarkantElementsModule } from '@markant/elements-angular';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { RegistrationHeaderComponent } from '@markant-platform/shared/registration';
import {
  RegexPattern,
  removeStorageKeysStartingWith,
  validateGLNCheckDigit,
} from '@markant-platform/shared/util-helpers';
import { UpgradeSuggestedStoragePrefix } from '@markant-platform/cartridge/model';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SupportFormErrorComponent } from './support-form-error/support-form-error.component';
import { ErrorPageService } from './error-page.service';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { CommonModule, NgOptimizedImage } from '@angular/common';

export enum PlatformAuthErrorType {
  NotActive = 'not-active',
  NotInvited = 'not-invited',
  Unexpected = 'unexpected-error',
}

@Component({
  standalone: true,
  selector: 'markant-platform-error-page',
  templateUrl: './error-page.component.html',
  styleUrls: ['./error-page.component.scss'],
  imports: [
    CommonModule,
    SharedFooterModule,
    MarkantElementsModule,
    TranslocoPipe,
    RegistrationHeaderComponent,
    ReactiveFormsModule,
    SupportFormErrorComponent,
    NgOptimizedImage,
    TranslocoDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorPageComponent implements OnInit {
  errorType!: string;
  isDialogOpen = false;
  loadingButton = false;
  formSent = false;

  loginErrorForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    companyName: new FormControl('', Validators.required),
    gln: new FormControl('', [Validators.pattern(RegexPattern.GLN_PATTERN), validateGLNCheckDigit]),
    description: new FormControl('', Validators.required),
  });

  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly errorPageService = inject(ErrorPageService);
  private readonly snackbarService = inject(SnackbarService);

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(queryParams => {
      const reason = queryParams['reason'] ?? PlatformAuthErrorType.Unexpected;
      this.errorType = this.mapReasonToErrorType(reason);

      if (queryParams['logout']) {
        const url = `${window.location.origin}/platform/error?reason=${encodeURI(reason)}`;

        this.authService.logout({ logoutParams: { returnTo: url } });
        removeStorageKeysStartingWith(UpgradeSuggestedStoragePrefix);
      }
    });
  }
  openContactSupportDialog(): void {
    this.isDialogOpen = true;
  }
  closeContactSupportDialog(): void {
    this.loginErrorForm.reset();
    this.loginErrorForm.markAsUntouched();
    this.isDialogOpen = false;
  }
  completeContactSupportDialog(): void {
    this.closeContactSupportDialog();
    this.formSent = false;
  }

  saveContactSupport(): void {
    if (this.loginErrorForm.invalid) {
      this.loginErrorForm.markAllAsTouched();
      return;
    }

    this.loadingButton = true;

    this.errorPageService.submitSupportForm(this.loginErrorForm.value).subscribe({
      next: () => {
        this.formSent = true;
      },
      complete: () => {
        this.loadingButton = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadingButton = false;
        this.snackbarService.showErrorSnackbarWithTransloco('common.somethingWentWrong');
      },
    });
  }

  private mapReasonToErrorType(reason: string): string {
    return (Object.values(PlatformAuthErrorType) as string[]).includes(reason)
      ? reason
      : PlatformAuthErrorType.Unexpected;
  }
}
